import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Calendar, Star, Tv, Film, Users, ExternalLink, Play, Clock, Plus, Share2, Loader2, Send, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function SeriesDetailsDialog({ open, onClose, loading, details, onAddToList }) {
  const isMovie = details?.media_type === 'movie';
  const [showSharePanel, setShowSharePanel] = useState(false);
  const [friends, setFriends] = useState([]);
  const [loadingFriends, setLoadingFriends] = useState(false);
  const [sharingWith, setSharingWith] = useState(null);
  const [shareMessage, setShareMessage] = useState('');
  
  // Fetch friends when share panel opens
  useEffect(() => {
    if (showSharePanel) {
      fetchFriends();
    }
  }, [showSharePanel]);
  
  const fetchFriends = async () => {
    setLoadingFriends(true);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/messages/friends`, { withCredentials: true, headers });
      setFriends(response.data.friends || []);
    } catch (error) {
      console.error('Failed to fetch friends:', error);
    } finally {
      setLoadingFriends(false);
    }
  };
  
  const handleShareWithFriend = async (friendId) => {
    setSharingWith(friendId);
    try {
      const token = localStorage.getItem('auth_token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.post(`${API}/messages`, {
        recipient_id: friendId,
        content: shareMessage || `Check out ${details.title}!`,
        message_type: 'media_share',
        media_data: {
          media_type: isMovie ? 'movie' : 'tv',
          item_id: details.tmdb_id?.toString() || details.id?.toString(),
          title: details.title,
          poster: details.poster_path,
          year: details.first_air_date ? new Date(details.first_air_date).getFullYear() : null
        }
      }, { withCredentials: true, headers });
      
      toast.success('Shared with friend!');
      setShowSharePanel(false);
      setShareMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to share');
    } finally {
      setSharingWith(null);
    }
  };
  
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] p-0 flex flex-col" data-testid="series-details-dialog">
        {loading ? (
          <div className="flex items-center justify-center py-12 px-6">
            <div className="w-8 h-8 border-4 border-[#F87171] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : details && (
          <>
            {/* Scrollable content area */}
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* Header: Poster + Basic Info */}
              <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                {details.poster_path ? (
                  <img 
                    src={details.poster_path} 
                    alt={details.title} 
                    className="w-24 h-36 sm:w-32 sm:h-48 object-cover rounded-xl shadow-lg mx-auto sm:mx-0 flex-shrink-0" 
                  />
                ) : (
                  <div className="w-24 h-36 sm:w-32 sm:h-48 bg-slate-200 rounded-xl flex items-center justify-center mx-auto sm:mx-0 flex-shrink-0">
                    {isMovie ? (
                      <Film className="w-10 h-10 sm:w-12 sm:h-12 text-slate-400" />
                    ) : (
                      <Tv className="w-10 h-10 sm:w-12 sm:h-12 text-slate-400" />
                    )}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h2 className="font-outfit text-xl sm:text-2xl font-bold text-[#3D4F6F] break-words">{details.title}</h2>
                    <span className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                      isMovie 
                        ? 'bg-purple-100 text-purple-700' 
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {isMovie ? 'Movie' : 'TV'}
                    </span>
                  </div>
                  {details.tagline && <p className="text-[#64748B] italic mb-3 text-sm sm:text-base">{details.tagline}</p>}
                  <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3">
                    {details.genres?.map((genre, i) => (
                      <Badge key={i} variant="secondary" className="bg-[#F87171]/10 text-[#F87171] text-xs">
                        {genre}
                      </Badge>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
                    {details.first_air_date && (
                      <div className="flex items-center gap-2 text-[#64748B]">
                        <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                        <span className="truncate">
                          {new Date(details.first_air_date).getFullYear()}
                          {!isMovie && details.last_air_date && details.status === 'Ended' && (
                            ` - ${new Date(details.last_air_date).getFullYear()}`
                          )}
                        </span>
                      </div>
                    )}
                    {details.vote_average > 0 && (
                      <div className="flex items-center gap-1.5 sm:gap-2 text-[#64748B]">
                        <a 
                          href={details.imdb_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 hover:text-[#F87171] transition-colors"
                        >
                          <img 
                            src="https://upload.wikimedia.org/wikipedia/commons/6/69/IMDB_Logo_2016.svg" 
                            alt="IMDb" 
                            className="h-3.5 sm:h-4"
                          />
                          <Star className="w-3.5 h-3.5 sm:w-4 sm:h-4 fill-amber-400 text-amber-400" />
                          <span>{details.vote_average.toFixed(1)}</span>
                          {details.imdb_url && <ExternalLink className="w-3 h-3 hidden sm:block" />}
                        </a>
                      </div>
                    )}
                    {isMovie && details.runtime && (
                      <div className="flex items-center gap-2 text-[#64748B]">
                        <Clock className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                        <span className="truncate">{details.runtime}</span>
                      </div>
                    )}
                    {!isMovie && details.number_of_seasons && (
                      <div className="flex items-center gap-2 text-[#64748B]">
                        <Tv className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                        <span>{details.number_of_seasons} {details.number_of_seasons === 1 ? 'Season' : 'Seasons'}</span>
                      </div>
                    )}
                    {details.status && (
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${details.status === 'Ended' || details.status === 'Released' ? 'border-slate-300' : 'border-green-500 text-green-600'}`}
                      >
                        {details.status}
                      </Badge>
                    )}
                  </div>
                  {isMovie && details.directors?.length > 0 && (
                    <div className="mt-3 text-sm text-[#64748B]">
                      <span className="font-medium">Director:</span> {details.directors.join(', ')}
                    </div>
                  )}
                </div>
              </div>

              {/* Overview */}
              {details.overview && (
                <div>
                  <h3 className="font-outfit font-semibold text-[#3D4F6F] mb-2">Overview</h3>
                  <p className="text-[#64748B] leading-relaxed text-sm">{details.overview}</p>
                </div>
              )}

              {/* Watch Providers */}
              {details.watch_providers && (details.watch_providers.flatrate?.length > 0 || details.watch_providers.buy?.length > 0) && (
                <div className="p-4 bg-slate-50 rounded-xl">
                  <h3 className="font-outfit font-semibold text-[#3D4F6F] mb-3 flex items-center gap-2">
                    <Play className="w-4 h-4" />
                    Where to Watch
                  </h3>
                  {details.watch_providers.flatrate?.length > 0 && (
                    <div className="mb-3">
                      <p className="text-xs text-[#64748B] mb-2 uppercase tracking-wide">Stream</p>
                      <div className="flex gap-2 flex-wrap">
                        {details.watch_providers.flatrate.map((provider) => (
                          <div key={provider.provider_id || provider.name} className="flex items-center gap-2 bg-white px-2 py-1.5 rounded-lg shadow-sm">
                            {provider.logo && (
                              <img src={provider.logo} alt={provider.name} className="w-5 h-5 rounded" />
                            )}
                            <span className="text-xs text-[#3D4F6F]">{provider.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {details.watch_providers.buy?.length > 0 && (
                    <div>
                      <p className="text-xs text-[#64748B] mb-2 uppercase tracking-wide">Buy / Rent</p>
                      <div className="flex gap-2 flex-wrap">
                        {details.watch_providers.buy.map((provider) => (
                          <div key={provider.provider_id || provider.name} className="flex items-center gap-2 bg-white px-2 py-1.5 rounded-lg shadow-sm">
                            {provider.logo && (
                              <img src={provider.logo} alt={provider.name} className="w-5 h-5 rounded" />
                            )}
                            <span className="text-xs text-[#3D4F6F]">{provider.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Cast */}
              {details.cast?.length > 0 && (
                <div>
                  <h3 className="font-outfit font-semibold text-[#3D4F6F] mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Cast
                  </h3>
                  <div className="flex gap-3 overflow-x-auto pb-2">
                    {details.cast.slice(0, 6).map((member) => (
                      <div key={member.id || member.name} className="flex-shrink-0 w-20 text-center">
                        {member.profile_path ? (
                          <img 
                            src={member.profile_path} 
                            alt={member.name} 
                            className="w-14 h-14 rounded-full object-cover mx-auto mb-1.5" 
                          />
                        ) : (
                          <div className="w-14 h-14 bg-slate-200 rounded-full mx-auto mb-1.5 flex items-center justify-center">
                            <Users className="w-5 h-5 text-slate-400" />
                          </div>
                        )}
                        <p className="text-xs font-medium text-[#3D4F6F] break-words leading-tight">{member.name}</p>
                        {member.character && (
                          <p className="text-xs text-[#94A3B8] break-words leading-tight mt-0.5">{member.character}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Networks/Production */}
              {!isMovie && details.networks?.length > 0 && (
                <div className="text-sm text-[#64748B]">
                  <span className="font-medium">Networks:</span> {details.networks.join(', ')}
                </div>
              )}
              {isMovie && details.production_companies?.length > 0 && (
                <div className="text-sm text-[#64748B]">
                  <span className="font-medium">Production:</span> {details.production_companies.join(', ')}
                </div>
              )}

              <p className="text-xs text-[#94A3B8]">
                Data powered by TMDB • Streaming availability varies by region
              </p>
            </div>

            {/* Fixed Action Buttons at bottom */}
            <div className="flex-shrink-0 border-t border-slate-200 p-4 bg-white">
              <div className="flex gap-3">
                {onAddToList && (
                  <Button
                    onClick={() => onAddToList(details)}
                    className="flex-1 bg-[#F87171] hover:bg-[#EF4444] text-white rounded-full h-10"
                    data-testid="add-to-list-from-details"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add to List
                  </Button>
                )}
                <Button
                  onClick={() => setShowSharePanel(!showSharePanel)}
                  variant="outline"
                  className={`rounded-full border-[#F87171] text-[#F87171] hover:bg-[#F87171]/10 h-10 ${onAddToList ? 'px-5' : 'flex-1'}`}
                  data-testid="share-with-friend-btn"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
              </div>
              
              {/* Share Panel */}
              {showSharePanel && (
                <div className="mt-3 p-3 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="flex items-center gap-2 mb-2">
                    <MessageSquare className="w-4 h-4 text-[#F87171]" />
                    <span className="font-medium text-sm text-[#3D4F6F]">Share with a friend</span>
                  </div>
                  <input
                    type="text"
                    value={shareMessage}
                    onChange={(e) => setShareMessage(e.target.value)}
                    placeholder={`Check out ${details?.title}!`}
                    className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg mb-2 focus:outline-none focus:ring-2 focus:ring-[#F87171]/30"
                    data-testid="share-message-input"
                  />
                  {loadingFriends ? (
                    <div className="flex justify-center py-3">
                      <Loader2 className="w-5 h-5 animate-spin text-[#F87171]" />
                    </div>
                  ) : friends.length === 0 ? (
                    <p className="text-sm text-[#64748B] text-center py-2">
                      No friends yet. Follow someone and get followed back!
                    </p>
                  ) : (
                    <div className="flex flex-wrap gap-2">
                      {friends.slice(0, 6).map((friend) => (
                        <button
                          key={friend.user_id}
                          onClick={() => handleShareWithFriend(friend.user_id)}
                          disabled={sharingWith === friend.user_id}
                          className="flex items-center gap-2 px-3 py-1.5 bg-white border border-slate-200 rounded-full hover:border-[#F87171] hover:bg-[#F87171]/5 transition-colors text-sm disabled:opacity-50"
                          data-testid={`share-friend-${friend.user_id}`}
                        >
                          {sharingWith === friend.user_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <>
                              <Avatar className="h-5 w-5">
                                {friend.avatar_url ? (
                                  <AvatarImage src={friend.avatar_url} />
                                ) : (
                                  <AvatarFallback className="bg-[#F87171]/10 text-[#F87171] text-xs">
                                    {friend.name?.charAt(0)}
                                  </AvatarFallback>
                                )}
                              </Avatar>
                              <span className="text-[#3D4F6F]">{friend.name?.split(' ')[0]}</span>
                              <Send className="w-3 h-3 text-[#F87171]" />
                            </>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
